import React, { useEffect, useState } from 'react';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useHamburgerMenu } from '../../Contexts/HamburgerMenuContext';
import XtramModal, { XtramButton, XtramNotice } from '../Widgets/XtramModal/XtramModal';
import FilterMonthYearPicker from '../Widgets/CustomDates/FilterMonthYearPicker/FilterMonthYearPicker';
import tb from '../ClientDetails/SubComponents/ClientDetailsHeader/HeaderToolbar.module.scss';
import PCNDocumentationModal from './PCNDocumentationModal';
import ReferenceContinuityNotice, { useReferenceContinuity } from './ReferenceContinuityNotice';
import styles from './PCNXtram.module.scss';

dayjs.extend(utc);

const STORAGE_KEY = 'pcnDate';
const BI_MONTHLY = 'דו חודשי';

const cx = (...classes) => classes.filter(Boolean).join(' ');
const toDate = (value) => value?.toDate?.() || value;

/** The picker's year change emits January 1st; keep the month already chosen. */
const withYear = (current, yearStart) => {
  const base = current ? new Date(toDate(current)) : new Date();
  const month = Number.isNaN(base.getTime()) ? 0 : base.getUTCMonth();
  return new Date(Date.UTC(yearStart.getUTCFullYear(), month, 1));
};

/** The current bi-monthly period: first day of its first month .. last day of its second month (UTC). */
const currentBiMonthlyPeriod = () => {
  const now = dayjs();
  const startMonth = now.month() - (now.month() % 2);
  return {
    start: dayjs.utc(new Date(Date.UTC(now.year(), startMonth, 1))),
    end: dayjs.utc(new Date(Date.UTC(now.year(), startMonth + 2, 0))),
  };
};

const readSavedPeriod = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
};

/**
 * Period and storno choice for the PCN874 file, then "check" or "download".
 * The period lives in the parent (it is shared with the checks modal); this modal only edits it.
 */
export default function PCNModal({
  open,
  onClose,
  direction = 'ltr',

  isDateRange,
  setIsDateRange,
  date, setDate,
  startDate, setStartDate,
  endDate, setEndDate,

  pcnIncludeStornoChecks,
  setPcnIncludeStornoChecks,

  pcnChecksLoading,
  handlePCNChecks,
  handlePCNDownload,

  t,

  caseId: propCaseId,
  token: propToken,
  viewDocumentDetails,
}) {
  const { menuProps } = useHamburgerMenu();
  const [showPcnDocumentation, setShowPcnDocumentation] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const client = menuProps?.client;
  const caseId = propCaseId || client?.caseId;
  const token = propToken || menuProps?.token;
  const isBiMonthlyClient = client?.reportingType === BI_MONTHLY;

  // On open: the last period used, or the current bi-monthly period for bi-monthly clients.
  useEffect(() => {
    if (!open) return;
    const saved = readSavedPeriod();
    if (saved) {
      setIsDateRange(!!saved.isDateRange);
      if (saved.isDateRange) {
        if (saved.startDate) setStartDate(dayjs(saved.startDate));
        if (saved.endDate) setEndDate(dayjs(saved.endDate));
      } else if (saved.date) {
        setDate(dayjs(saved.date));
      }
      return;
    }
    if (isBiMonthlyClient) {
      const period = currentBiMonthlyPeriod();
      setIsDateRange(true);
      setStartDate(period.start);
      setEndDate(period.end);
    }
    // Only when the modal opens; later client refreshes must not reset the chosen period.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const iso = (value) => toDate(value)?.toISOString?.() ?? value;
    const payload = isDateRange
      ? { isDateRange: true, startDate: iso(startDate), endDate: iso(endDate) }
      : { isDateRange: false, date: iso(date) };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  }, [open, isDateRange, date, startDate, endDate]);

  const periodStart = toDate(isDateRange ? startDate : date);
  const periodEnd = toDate(isDateRange ? endDate : date);
  const invalidRange = isDateRange && periodStart && periodEnd && new Date(periodStart) > new Date(periodEnd);

  // The reference sequence is checked over the whole year of the period, so only the year matters.
  const year = periodStart ? dayjs(periodStart).year() : null;
  const referenceResult = useReferenceContinuity({
    open,
    caseId,
    token,
    start: year != null ? new Date(Date.UTC(year, 0, 1)) : null,
    end: year != null ? new Date(Date.UTC(year, 11, 31)) : null,
    delay: 400,
  });

  const runChecks = () => {
    if (invalidRange) return;
    handlePCNChecks(periodStart, periodEnd);
  };

  const runDownload = async () => {
    if (invalidRange || pcnIncludeStornoChecks === null) return;
    setDownloading(true);
    try {
      await handlePCNDownload(periodStart, periodEnd);
    } finally {
      setDownloading(false);
    }
  };

  const busy = pcnChecksLoading || downloading;

  const footer = (
    <>
      <XtramButton variant="secondary" onClick={onClose} disabled={busy}>
        {t('Close')}
      </XtramButton>
      <div className={styles.footerSpacer} />
      <XtramButton variant="secondary" onClick={runChecks} busy={pcnChecksLoading} disabled={busy || invalidRange}>
        <FactCheckOutlinedIcon sx={{ fontSize: 18 }} />
        {t('Checkss')}
      </XtramButton>
      <XtramButton
        onClick={runDownload}
        busy={downloading}
        disabled={busy || invalidRange || pcnIncludeStornoChecks === null}
      >
        <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />
        {t('Download')}
      </XtramButton>
    </>
  );

  return (
    <>
      <PCNDocumentationModal open={showPcnDocumentation} onClose={() => setShowPcnDocumentation(false)} />

      <XtramModal open={open} onClose={onClose} title="PCN874" direction={direction} busy={downloading} footer={footer}>
        <div className={styles.stack}>
          <div className={styles.topBar}>
            <span className={styles.muted}>{t('SelectDateRange')}</span>
            <XtramButton variant="ghost" small onClick={() => setShowPcnDocumentation(true)}>
              <MenuBookOutlinedIcon sx={{ fontSize: 16 }} />
              {t('PCNDocumentation')}
            </XtramButton>
          </div>

          <ReferenceContinuityNotice
            t={t}
            result={referenceResult}
            onViewIncomes={viewDocumentDetails ? () => { onClose(); viewDocumentDetails('Incomes'); } : undefined}
          />

          <div className={styles.panel}>
            <div className={styles.panelHeader}>
              <span>{t('ReportingPeriod')}</span>
              <div className={styles.segmented}>
                <button
                  type="button"
                  className={cx(styles.segment, !isDateRange && styles.segmentActive)}
                  onClick={() => setIsDateRange(false)}
                >
                  {t('SingleDate')}
                </button>
                <button
                  type="button"
                  className={cx(styles.segment, isDateRange && styles.segmentActive)}
                  onClick={() => setIsDateRange(true)}
                >
                  {t('DateRange')}
                </button>
              </div>
            </div>
            <div className={styles.panelBody}>
              {isDateRange ? (
                <div className={tb.dateSelectRange}>
                  <div className={tb.dateSelect}>
                    <FilterMonthYearPicker
                      year={toDate(startDate)}
                      month={toDate(startDate)}
                      onYearChange={(v) => setStartDate(withYear(startDate, v))}
                      onMonthChange={setStartDate}
                      labelYear={t('StartYear')}
                      labelMonth={t('StartMonth')}
                      direction={direction}
                    />
                  </div>
                  <div className={tb.rangeDivider} />
                  <div className={tb.dateSelect}>
                    <FilterMonthYearPicker
                      year={toDate(endDate)}
                      month={toDate(endDate)}
                      onYearChange={(v) => setEndDate(withYear(endDate, v))}
                      onMonthChange={setEndDate}
                      labelYear={t('EndYear')}
                      labelMonth={t('EndMonth')}
                      direction={direction}
                    />
                  </div>
                </div>
              ) : (
                <div className={styles.periodRow}>
                  <div className={tb.dateSelect}>
                    <FilterMonthYearPicker
                      year={toDate(date)}
                      month={toDate(date)}
                      onYearChange={(v) => setDate(withYear(date, v))}
                      onMonthChange={setDate}
                      labelYear={t('Year')}
                      labelMonth={t('Month')}
                      direction={direction}
                    />
                  </div>
                </div>
              )}
              {invalidRange && <XtramNotice type="error">{t('PCNStartAfterEnd')}</XtramNotice>}
            </div>
          </div>

          <div className={styles.panel}>
            <div className={styles.panelHeader}>
              <span>{t('SelectStornoOption')}</span>
            </div>
            <div className={styles.panelBody}>
              <div className={styles.optionsRow} role="radiogroup">
                {[
                  { value: false, label: t('ExcludingStorno_Checks') },
                  { value: true, label: t('IncludingStorno_Checks') },
                ].map((option) => {
                  const active = pcnIncludeStornoChecks === option.value;
                  return (
                    <button
                      key={String(option.value)}
                      type="button"
                      role="radio"
                      aria-checked={active}
                      className={cx(styles.option, active && styles.optionActive)}
                      onClick={() => setPcnIncludeStornoChecks(option.value)}
                    >
                      <span className={styles.optionDot} />
                      {option.label}
                    </button>
                  );
                })}
              </div>
              {pcnIncludeStornoChecks === null && <span className={styles.muted}>{t('PCNChooseStornoToDownload')}</span>}
            </div>
          </div>
        </div>
      </XtramModal>
    </>
  );
}
